<template>
  <div>
    <NavTop/>
    <NavDark/>

    <section v-if="!isSearchMode" class="bg-cover"
             :style="{
        backgroundImage: `linear-gradient(rgba(242,242,242,0), rgba(242,242,242,0)), url(${heroBg})`,
        backgroundRepeat:'no-repeat',
        backgroundSize:'cover',
        backgroundPosition:'center',
        backgroundColor:'#222222'
      }"
             aria-label="Kategorioversigt">
      <div class="container">
        <div class="row align-items-center justify-content-center">
          <div class="col-xl-12 col-lg-12 col-md-12 col-sm-12">
            <div class="text-center py-5 mt-3 mb-3">
              <h1 class="ft-medium mb-3">{{ activeCategory?.displayname }}</h1>
              <nav v-if="activeSubCategories.length" aria-label="Underkategorier">
                <ul class="shop_categories_list m-0 p-0">
                  <li v-for="sub in activeSubCategories" :key="sub.name">
                    <a href="#"
                       role="button"
                       @click.prevent="applySubcategory(sub)"
                       :class="{ 'active': activeSubcat && activeSubcat.urlSlug === sub.urlSlug }"
                       :aria-current="activeSubcat && activeSubcat.urlSlug === sub.urlSlug ? 'true' : null"
                       :aria-label="'Filtrér efter ' + sub.displayname"
                       :title="'Filtrér efter ' + sub.displayname">
                      {{ sub.displayname }}
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-else-if="isSearchMode" class="bg-light" aria-label="Søgeresultater">
      <div class="container">
        <div class="row align-items-center justify-content-center">
          <div class="col-xl-12 col-lg-12 col-md-12 col-sm-12">
            <div class="text-center py-5 mt-3 mb-3">
              <h1 class="ft-medium mb-3">Søgeresultater</h1>
              <h4 v-if="searchQuery" class="ft-regular">
                Viser resultater for "<strong>{{ searchQuery }}</strong>"
              </h4>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FilterOne @sort-change="handleSortChange" aria-label="Filtrering og sortering"/>

    <section class="middle" aria-label="Produktliste">
      <div class="container">
        <ProductGrid :products="displayedProducts" :loading="isLoading" />

        <div class="row" v-if="canLoadMore">
          <div class="col-xl-12 col-lg-12 col-md-12 text-center">
            <button
                @click="loadMore"
                :disabled="!canLoadMore || isLoading"
                class="btn stretched-links borders m-auto d-inline-flex align-items-center"
                type="button"
                aria-label="Hent flere produkter">
              <i class="lni lni-reload me-2" aria-hidden="true"></i>Hent flere
            </button>
          </div>
        </div>
      </div>
    </section>

    <FooterTopOne/>
    <FooterDark/>
    <ScrollToTop/>
  </div>
</template>

<script setup>
import FilterOne from '@/components/filter/filter-one.vue'
import FooterDark from '@/components/footer/footer-dark.vue'
import FooterTopOne from '@/components/footer/footer-top-one.vue'
import NavDark from '@/components/navbar/nav-dark.vue'
import ScrollToTop from '@/components/scroll-to-top.vue'

import { Categories, defaultHero } from '@/data/data'
import { ref, computed, watch, defineExpose, onMounted, defineProps } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import NavTop from '@/components/navbar/nav-top.vue'
import { useProductStore, makeSignature } from '@/stores/useProductStore'
import ProductGrid from '@/components/product/product-grid.vue'

import { updateMeta } from '@/utils/meta.js'

const props = defineProps({
  category: { type: String, default: '' },
  sub: { type: String, default: '' }
})

const sortKey = ref('default')
const route = useRoute()
const router = useRouter()
const store = useProductStore()

const PAGE_LIMIT = 999

const normalize = (s) =>
    String(s ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .trim()

const searchQuery = computed(() => String(route.query.search ?? ''))
const isSearchMode = computed(() => !!searchQuery.value && !props.category)

const activeCategoryName = computed(() => normalize(decodeURIComponent(props.category)))
const activeCategory = computed(() => {
  if (isSearchMode.value) return null; // Don't run in search mode
  const cats = Array.isArray(Categories) ? Categories : []
  return cats.find(c => normalize(c.name) === activeCategoryName.value) || null
})
const activeSubCategories = computed(() => activeCategory.value?.subCategories ?? [])
const activeSubSlug = computed(() => String(props.sub || ''))
const activeSubcat = computed(() =>
    activeSubCategories.value.find(s => s.urlSlug === activeSubSlug.value) || null
)

onMounted(() => {
  if (!isSearchMode.value) {
    if (!activeCategory.value) {
      router.replace('/404')
      return
    }
    if (!activeSubSlug.value && activeSubCategories.value.length) {
      router.replace({
        path: `/shop/${activeCategory.value.name}/${activeSubCategories.value[0].urlSlug}`,
        query: route.query
      })
      return
    }
    if (!activeSubcat.value) {
      router.replace('/404')
    }
  }
})

// navigate to subcategory
const applySubcategory = (sub) => {
  if (!activeCategory.value) return
  router.push({
    path: `/shop/${activeCategory.value.name}/${sub.urlSlug}`,
    query: route.query
  })
}

// Filter query (for use within a category, not global search)
const qParam = computed(() => String(route.query.q ?? ''))

const serverSort = computed(() => {
  switch (sortKey.value) {
    case 'price-asc': return 'price_asc'
    case 'price-desc': return 'price_desc'
    case 'new': return 'newest'
    default: return 'random'
  }
})

const shouldUseSeed = computed(() =>
    serverSort.value === 'random' && qParam.value === '' && searchQuery.value === ''
)

const getSeed = () => new Date().toISOString().slice(0, 10)

const currentViewSignature = computed(() => {
  const sort = serverSort.value;
  const seed = shouldUseSeed.value ? getSeed() : null;

  if (isSearchMode.value) {
    // Search Mode Signature
    return makeSignature('all', {
      q: searchQuery.value, // Use the 'search' query param as 'q' for the API
      sort: sort
    });
  } else {
    // Category Mode Signature
    return makeSignature('categories', {
      category_id: activeSubcat.value?.id ?? null,
      q: qParam.value, // This is the filter 'q'
      sort: sort,
      seed: seed,
    });
  }
})

// New loader for Search Mode
const loadForSearch = async () => {
  await store.loadAll({
    q: searchQuery.value, // Pass 'search' as 'q'
    limit: PAGE_LIMIT,
    offset: 0,
    sort: serverSort.value,
  });
}

// Original loader for Category Mode
const loadForCategory = async () => {
  if (!activeSubcat.value?.id) return
  await store.loadCategory({
    categoryId: activeSubcat.value.id,
    limit: PAGE_LIMIT,
    offset: 0,
    sort: serverSort.value,
    q: qParam.value,
    seed: shouldUseSeed.value ? getSeed() : null,
  })
}

// Updated Watcher to handle both modes
watch(
    currentViewSignature,
    (newSig, oldSig) => {
      if (newSig === oldSig) return; // Don't reload if signature hasn't changed

      if (isSearchMode.value) {
        loadForSearch();
      } else {
        loadForCategory();
      }
    },
    { immediate: true }
)

const productsFromStore = computed(() => Array.isArray(store.productData) ? store.productData : [])
const displayedProducts = computed(() => productsFromStore.value)

const canLoadMore = computed(() => store.hasMore && !store.isLoading)
const isLoading = computed(() => store.isLoading)

const handleSortChange = (key) => { sortKey.value = key || 'default' }

const loadMore = () => {
  if (!canLoadMore.value) return
  store.loadMore({ add: PAGE_LIMIT })
}

const heroBg = computed(() => activeCategory.value?.hero || defaultHero)

defineExpose({ loadMore, isLoading, canLoadMore })

watch(
    [isLoading, activeSubcat, isSearchMode, searchQuery],
    ([loading, subcat, search, query]) => {

      if (loading) {
        return;
      }

      if (search) {
        const title = `Søgeresultat: "${query}"`;
        const description = `Viser søgeresultater for "${query}" på YOKO.dk`;
        updateMeta(title, description);
      } else if (subcat) {
        updateMeta(subcat.metaTitle, subcat.metaDescription);
      } else if (activeCategory.value) {
        updateMeta(activeCategory.value.displayname, `Se alle produkter i kategorien ${activeCategory.value.displayname}`);
      }
    },
    {
      immediate: true,
      deep: true
    }
);
</script>